/**
 * Друкована форма «Акт надання послуг» (рішення власника 2026-07-26).
 *
 * Двосторонній акт: Виконавець (організація) / Замовник (контрагент),
 * договір (з повагою до print_deal_info/print_name картки), період надання,
 * таблиця послуг, разом/ПДВ (неплатник — без колонок ПДВ), сума прописом,
 * підписи сторін. Прецедент: CustomerInvoicePrintPage.
 *
 * Роут: /service-acts/:id/print (кнопка «друк» у редакторі акта).
 */
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { serviceActs } from '@/api/serviceActs';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import PrintActions from '@/shared/components/PrintActions';
import PrintLines1c, { type PrintLine } from '@/shared/components/PrintLines1c';
import { printLineAmount, vatMode } from '@/shared/utils/vatFlags';
import { partyCodesText } from '@/shared/utils/partyCodes';


export default function ServiceActPrintPage() {
  const { id } = useParams<{ id: string }>();

  // Друк за ЄДИНОЮ механікою друкованих форм (body.print-mode +
  // .print-page): у друк йде ЛИШЕ бланк, без layout застосунку.
  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);



  const docQ = useQuery({
    queryKey: ['service-act', id],
    queryFn: () => serviceActs.get(id!),
    enabled: !!id,
  });
  const d = docQ.data;

  const orgQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgQ.data?.items.find((o) => o.id === d?.organization_id);
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const contractor = contractorsQ.data?.items.find((c) => c.id === d?.contractor_id);
  const contractsQ = useQuery({
    queryKey: ['contracts', d?.organization_id, d?.contractor_id],
    queryFn: () => refs.contracts({
      organization_id: d!.organization_id, contractor_id: d!.contractor_id,
    }),
    enabled: !!d,
  });
  const contract = contractsQ.data?.find((c) => c.id === d?.contract_id);
  const nomListQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });
  const uomsQ = useQuery({ queryKey: ['uoms'], queryFn: refs.uoms });

  if (!d || !org) {
    return <div style={{ padding: '2rem' }}>Завантаження акта…</div>;
  }

  const nomById = new Map((nomListQ.data?.items ?? []).map((n) => [n.id, n]));
  const uomById = new Map((uomsQ.data ?? []).map((u) => [u.id, u.short_name]));
  // ч.1374: таблиця, підсумки і прописом — за ознаками ПДВ документа (як у 1С).
  const mode = vatMode(d, org.is_vat_payer);
  const net = d.items.reduce((s, it) => s + Number(it.amount_without_vat), 0);
  const vat = mode === 'none' ? 0 : d.items.reduce((s, it) => s + Number(it.vat_amount), 0);
  const lines: PrintLine[] = d.items.map((it) => {
    const nom = nomById.get(it.nomenclature_id);
    return {
      name: `${nom?.name ?? '—'}${it.description ? ` — ${it.description}` : ''}`,
      qty: Number(it.quantity),
      uom: it.uom_id ? (uomById.get(it.uom_id) ?? 'посл.')
        : nom?.base_uom_id ? (uomById.get(nom.base_uom_id) ?? 'посл.') : 'посл.',
      price: Number(it.price),
      amount: printLineAmount(it, mode),
      discount: Number(it.discount_percent ?? 0) || null,
    };
  });

  const contractLine = contract && contract.print_deal_info !== false
    ? (contract.print_name
      || `${contract.name}${contract.number ? ` №${contract.number}` : ''}`)
    : null;
  const period = d.service_period_from && d.service_period_to
    ? `${new Date(d.service_period_from).toLocaleDateString('uk-UA')} — ${new Date(d.service_period_to).toLocaleDateString('uk-UA')}`
    : null;

  return (
    <div className="print-page invoice-print" style={{
      maxWidth: 800, margin: '0 auto', padding: '1.5rem',
      fontFamily: "'Times New Roman', serif", fontSize: '10.5pt', background: 'var(--bg-panel)',
    }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white; }
          @page { size: A4; margin: 1.5cm; }
        }
      `}</style>

      <PrintActions docMeta={{
        organizationId: d.organization_id,
        documentType: 'doc.service_acts',
        documentId: d.id,
        filename: `Акт послуг ${d.document_number}`,
        orgName: org.name,
        contractorEmail: contractor?.email ?? null,
      }} />

      <h2 style={{ textAlign: 'center', margin: '0.75rem 0' }}>
        Акт надання послуг № {d.document_number}
        {' '}від {new Date(d.document_date).toLocaleDateString('uk-UA')}
      </h2>

      <table style={{ width: '100%', fontSize: '10.5pt', marginBottom: 8 }}>
        <tbody>
          <tr>
            <td style={{ width: 110, verticalAlign: 'top', color: '#333' }}>Виконавець:</td>
            <td>
              <strong>{org.full_name || org.name}</strong><br />
              {partyCodesText(org) && <>{partyCodesText(org)}{', '}</>}
              {mode === 'none' && !org.is_vat_payer && <>неплатник ПДВ{', '}</>}
              {org.legal_address ?? ''}
            </td>
          </tr>
          <tr>
            <td style={{ verticalAlign: 'top', color: '#333', paddingTop: 6 }}>Замовник:</td>
            <td style={{ paddingTop: 6 }}>
              <strong>{contractor?.name ?? '—'}</strong>
              {partyCodesText(contractor) && <>{', '}{partyCodesText(contractor)}</>}
              {contractor?.legal_address && <><br />{contractor.legal_address}</>}
            </td>
          </tr>
          {contractLine && (
            <tr>
              <td style={{ color: '#333', paddingTop: 6 }}>Договір:</td>
              <td style={{ paddingTop: 6 }}>{contractLine}</td>
            </tr>
          )}
          {period && (
            <tr>
              <td style={{ color: '#333', paddingTop: 6 }}>Період:</td>
              <td style={{ paddingTop: 6 }}>{period}</td>
            </tr>
          )}
        </tbody>
      </table>

      <p style={{ margin: '0.5rem 0' }}>
        Ми, що нижче підписалися, представник Замовника з одного боку і
        представник Виконавця з іншого боку, склали цей акт про те, що
        Виконавець надав, а Замовник прийняв такі послуги:
      </p>

      <PrintLines1c mode={mode} lines={lines} net={net} vat={vat}
        nameHeader="Найменування робіт (послуг)" countLabel="Всього надано послуг" />

      <p style={{ margin: '0.5rem 0' }}>
        Сторони претензій одна до одної не мають. Послуги надані в повному
        обсязі, вчасно та належної якості.
      </p>

      <table style={{ width: '100%', marginTop: 28, fontSize: '10.5pt' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Від Виконавця</strong><br /><br />
              _______________________
              {org.director_name && (
                <><br /><span style={{ color: '#333' }}>({org.director_name})</span></>
              )}
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, М.П.</span>
            </td>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Від Замовника</strong><br /><br />
              _______________________
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, М.П.</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
